import isValid from 'date-fns/isValid'
import format from 'date-fns/format'
import { formatISO, isAfter, isBefore, parse, setHours } from 'date-fns'
import { IntlShape } from 'react-intl'

import { IDateRangeFilter, IDateRangeAsString } from 'features/App/components/Filters/DateRangeFilter/types'
import { pad2 } from 'utils/string'
import { GTWeekday } from 'gql/types.generated'

export const DEFAULT_TIMEZONE = 'America/Mexico_City'
export const DATE_FORMAT = 'dd/MM/yyyy'
export const US_DATE_FORMAT = 'yyyy-MM-dd'

export interface ISplittedTime {
  hours: string
  minutes: string
}

export interface ITimeZoneNameObject {
  short: string | undefined
  long: string | undefined
  shortOffset: string | undefined
  longOffset: string | undefined
}

export enum ISOVariations {
  COMPLETE = 'complete',
  DATE = 'date',
  TIME = 'time'
}

export const dateToString = (date: Date | undefined | '', timeZone?: string): string => {
  if (!date) return ''

  date = new Date(date)

  if (!isFinite(date.getTime())) {
    return 'Invalid Date'
  }

  return new Intl.DateTimeFormat('es-MX', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone
  }).format(date)
}

/**
 * Formats a date string to a specified timezone and returns it as a formatted string.
 *
 * @param {string} dateString - The date string to format.
 * @param {string} [timezone] - The timezone to format the date string to.
 * @returns {string} The formatted date string.
 *
 * @example
 * // Formatting a date string to a specific timezone
 * const formattedDate = formatDateWithTimeZone('2025-03-11T10:00:00Z', 'America/Mexico_City');  // returns "11/03/2025, 04:00 AM"
 *
 * @example
 * // Handling invalid date string
 * const formattedDate = formatDateWithTimeZone('invalid-date', 'America/Mexico_City');  // returns "Invalid Date"
 */
export const formatDateWithTimezone = (dateString: string, timezone?: string): string => {
  const date = new Date(dateString)

  if (!isFinite(date.getTime())) {
    return 'Invalid Date'
  }

  return new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  })
    .format(date)
    .replace(/\.\s?/g, '')
    .toUpperCase()
}

export const dateRangeToString = (intl: IntlShape, { from, to }: IDateRangeFilter): string => {
  const fromValue = dateToString(from)
  const toValue = dateToString(to)

  if (fromValue && toValue) {
    return `${fromValue} - ${toValue}`
  } else if (fromValue) {
    return `From ${fromValue}`
  } else if (toValue) {
    return `To ${toValue}`
  }

  return ''
}

export const initDateByString = (string: string): Date | null => {
  if (string) {
    const array = string.split('/')
    const day = parseInt(array[0])
    const month = parseInt(array[1]) - 1
    const year = parseInt(array[2])

    return new Date(year, month, day)
  }

  return null
}

export const getHoursByDuration = (duration: number): number => {
  return Math.trunc(duration / 60)
}

export const sanitizeDate = (date = ''): Date | '' => {
  const dateValue = initDateByString(date)

  if (isValid(dateValue)) {
    return dateValue as Date
  }

  return ''
}

export const getTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE
}

export const toLocalISOString = (date: Date): string => {
  return (
    date.getFullYear() +
    '-' +
    pad2(date.getMonth() + 1) +
    '-' +
    pad2(date.getDate()) +
    'T' +
    pad2(date.getHours()) +
    ':' +
    pad2(date.getMinutes()) +
    ':' +
    pad2(date.getSeconds())
  )
}

export const getDateRangeQuery = (dateRange: IDateRangeFilter): IDateRangeAsString => {
  if (dateRange.rangeValue) {
    return {
      rangeValue: dateRange.rangeValue
    }
  }

  return {
    from: dateRange.from && format(dateRange.from, DATE_FORMAT),
    to: dateRange.to && format(dateRange.to, DATE_FORMAT)
  }
}

export const getTimeAsMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':')

  return parseInt(hours) * 60 + parseInt(minutes)
}

export const splitTime = (timeStr: string): ISplittedTime => {
  if (typeof timeStr !== 'string' || timeStr.length !== 5) {
    throw new Error('input should be HH:mm format')
  }
  const [hours, minutes] = timeStr.split(':')

  return {
    hours,
    minutes
  }
}

export function moveClockFoward(time: string, duration: number): Date {
  const incrementMinutesToDate = (date: Date, hours: number): Date => {
    return new Date(new Date().setTime(date.getTime() + hours * 60 * 1000))
  }

  const { hours, minutes } = splitTime(time)
  const startTime = new Date(setHours(new Date(), parseInt(hours)).setMinutes(parseInt(minutes)))
  const date = incrementMinutesToDate(startTime, duration)

  return date
}

export function getTimeZoneName(timeZone: string): ITimeZoneNameObject {
  const getByType = (
    type: 'short' | 'long' | 'shortOffset' | 'longOffset' | 'shortGeneric' | 'longGeneric' | undefined
  ): string | undefined => {
    const TIME_ZONE_INDEX = 6
    const dateTimeStr = Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: type })
    return dateTimeStr?.formatToParts()?.at(TIME_ZONE_INDEX)?.value
  }
  return {
    short: getByType('short'),
    long: getByType('long'),
    shortOffset: getByType('shortOffset'),
    longOffset: getByType('longOffset')
  }
}

export const parseDate = (date: string): Date => {
  return parse(date, US_DATE_FORMAT, new Date())
}

export const formatISOComplete = (date: Date): string => {
  return formatISO(date, { representation: ISOVariations.COMPLETE })
}

export const formatISODate = (date: Date): string => {
  return formatISO(date, { representation: ISOVariations.DATE })
}

export const formatISOTime = (date: Date): string => {
  return formatISO(date, { representation: ISOVariations.TIME })
}

export const getWeekday = (value: number): GTWeekday => {
  const weekdayIndex = value - 1
  const weekdays = [
    GTWeekday.Monday,
    GTWeekday.Tuesday,
    GTWeekday.Wednesday,
    GTWeekday.Thursday,
    GTWeekday.Friday,
    GTWeekday.Saturday,
    GTWeekday.Sunday
  ]
  if (weekdayIndex >= 0 && weekdayIndex <= 6) {
    return weekdays[weekdayIndex]
  }

  return GTWeekday.Sunday
}

export const getWeekdayNumber = (value: GTWeekday): number => {
  const weekdays = [
    GTWeekday.Monday,
    GTWeekday.Tuesday,
    GTWeekday.Wednesday,
    GTWeekday.Thursday,
    GTWeekday.Friday,
    GTWeekday.Saturday,
    GTWeekday.Sunday
  ]

  return weekdays.indexOf(value) + 1
}

export const getHourMinuteByTimeAndDay = (time: string, day: string | number): number[] => {
  const [hourStr, minuteStr] = time.split(':')
  const dayNumber = parseInt(day as string)
  const hour = parseInt(hourStr) + 24 * dayNumber
  const minute = parseInt(minuteStr)
  return [hour, minute]
}

export const isSameDay = (date1: string, date2: string): boolean => {
  let firstDate = new Date(date1)
  firstDate = new Date(firstDate.getFullYear(), firstDate.getMonth(), firstDate.getDate())

  let secondDate = new Date(date2)
  secondDate = new Date(secondDate.getFullYear(), secondDate.getMonth(), secondDate.getDate())

  return firstDate === secondDate
}

export interface IDateRange {
  startDate: Date
  endDate?: Date | null
}
export const updateDateRangeByStartDate = (dateRange: IDateRange, newStartDate: Date): IDateRange => {
  return {
    startDate: newStartDate,
    endDate: dateRange.endDate && isAfter(newStartDate, dateRange.endDate) ? newStartDate : dateRange.endDate
  }
}

export const updateDateRangeByEndDate = (dateRange: IDateRange, newEndDate?: Date | null): IDateRange => {
  return {
    startDate: newEndDate && isBefore(newEndDate as Date, dateRange.startDate) ? newEndDate : dateRange.startDate,
    endDate: newEndDate
  }
}
